import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Product } from '#bv/data/products'
import { BundleTierList } from '#bv/components/BundleTierList'
import { getMergedCatalogFn } from '#bv/lib/productCatalog'
import { useProductStock } from '#bv/lib/useProductStock'

type Props = {
  product: Product
  open: boolean
  onClose: () => void
}

export function BundleTierModal({ product, open, onClose }: Props) {
  const picker = product.bundlePicker
  const { inStock } = useProductStock()
  const [catalog, setCatalog] = useState<Product[]>([])
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open || !picker) return
    let active = true
    void getMergedCatalogFn().then((list) => {
      if (active) setCatalog(list)
    })
    return () => {
      active = false
    }
  }, [open, picker])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  if (!open || !picker) return null

  const tierCount = picker.tiers.length

  const modal = (
    <div className="fixed inset-0 z-[1200]" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-[#050508]/80 backdrop-blur-[6px]"
        aria-label="Close tier picker"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bundle-tier-title"
        className="fixed inset-x-0 z-[1201] flex w-full max-w-lg min-h-0 flex-col overflow-hidden rounded-t-[1.35rem] border border-white/[0.12] shadow-2xl top-[max(0px,env(safe-area-inset-top))] bottom-[max(0px,env(safe-area-inset-bottom))] sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:w-[min(100%-3rem,32rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:max-h-[min(88vh,720px)]"
        style={{
          background:
            'linear-gradient(168deg, rgba(28,24,34,0.98) 0%, rgba(12,10,16,0.99) 55%, rgba(8,7,11,1) 100%)',
          boxShadow:
            '0 -12px 48px rgba(0,0,0,0.55), 0 24px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 justify-center pt-2.5 sm:hidden" aria-hidden>
          <span className="h-1 w-10 rounded-full bg-white/20" />
        </div>

        <div className="relative shrink-0 border-b border-white/[0.08] px-5 pb-4 pt-3 sm:px-6 sm:pt-5">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40 text-lg leading-none text-[#8ecfff] transition-colors hover:border-[#00bfff]/40 hover:bg-[#00bfff]/15 hover:text-white sm:right-4 sm:top-4"
            aria-label="Close tier picker"
          >
            ×
          </button>

          <p className="m-0 text-[0.62rem] font-bold uppercase tracking-[0.18em] text-[#93c5fd]">
            {tierCount} {tierCount === 1 ? 'option' : 'options'}
          </p>
          <h2 id="bundle-tier-title" className="font-display mt-1 pr-10 text-xl tracking-wide text-[#f0f6ff] sm:text-2xl">
            {picker.title}
          </h2>
          {picker.description ? (
            <p className="mt-2 max-w-prose text-sm leading-relaxed text-[#7a92b8]">{picker.description}</p>
          ) : null}
          <p className="mt-2 hidden text-[0.65rem] leading-snug text-[#5a6d8a] sm:block">
            Pick a tier to view details and checkout. Tap outside or press Esc to close.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-3 [-webkit-overflow-scrolling:touch] sm:px-6 sm:py-4">
          <BundleTierList tiers={picker.tiers} catalog={catalog} inStock={inStock} layout="modal" />
        </div>

        <div className="shrink-0 border-t border-white/[0.08] px-5 py-3 sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-chrome w-full rounded-xl py-2.5 text-sm"
          >
            Back to shop
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
